const EntityStatus = require('@/lib/Entities/EntityStatus');
const moment = require('moment');

// This file contains functions used by TodayViewLinearLayout.vue extracted to simplify testing.

/**
 * @private
 *
 * Assign sessions for a day for an event into an array of tracks (rows) to ensure that no session overlaps another
 * session on the same track. A track can contain sequential sessions as long as they don't overlap.
 *
 * @param {Session[]} sessions
 *
 * @return {Session[][]}
 */
const assignSessionsToTracks = (sessions) => {
    if (sessions.length === 0) {
        return [];
    }

    const sortedSessions = [...sessions].sort((a, b) => a.startTimeMinutes - b.startTimeMinutes);

    const tracks = [];

    sortedSessions.forEach((session) => {
        let placedInTrack = false;

        for (let i = 0; i < tracks.length; i++) {
            if (canPlaceInTrack(session, tracks[i])) {
                tracks[i].push(session);
                placedInTrack = true;
                break;
            }
        }

        if (!placedInTrack) {
            tracks.push([session]);
        }
    });

    return tracks;
};

/**
 * Calculate session tracks for events to prevent overlapping sessions.
 *
 * @param {Object.<number, Event>} events - Object with eventIDs as keys and Event objects as values.
 * @param {Object.<number, SessionAvailability>} sessionAvailabilities - Object with session IDs as keys and SessionAvailability objects as values.
 * @param {Object.<number, Session[]>} sortedSessions - Object with eventIDs as keys and arrays of Session objects as values.
 *
 * @return {{trackCountByEvent: Object.<number, number>, tracksByEventAndCell: Object.<number, Object.<number, Array[]>>}}
 */
const calculateSessionTracks = (events, sessionAvailabilities, sortedSessions) => {
    const tracksByEventAndCell = {};
    const trackCountByEvent = {};

    Object.keys(sortedSessions).forEach((eventID) => {
        const event = events[eventID];
        if (!event) {
            return;
        }

        const sessions = sortedSessions[eventID];

        // Prepare all sessions with their time data.
        const preparedSessions = sessions.map((session) => {
            const momentStartTime = moment(session.startTime);
            const startHour = momentStartTime.hour();
            const startMinute = momentStartTime.minute();

            let endTime = session.endTime;
            if (!endTime) {
                if (event.sessionType === 'Date-based') {
                    endTime = momentStartTime.clone().endOf('day').toDate();
                } else if (event.sessionType === 'Time-based') {
                    endTime = momentStartTime.clone().add(1, 'hour').toDate();
                }
            }

            const durationInMinutes = getDurationInMinutes(session.startTime, endTime);
            const startTimeMinutes = startHour * 60 + startMinute;
            const endTimeMinutes = startTimeMinutes + durationInMinutes;

            const sessionAvailability = sessionAvailabilities[session.ID];
            let sessionClass = '';

            if (sessionAvailability) {
                sessionClass = 'linear-view-table__cell-session-'
                    + getAvailabilityState(session, sessionAvailability, event);

                if (sessionAvailability.spacesInCart && sessionAvailability.spacesInCart !== 0) {
                    sessionClass += ' chip chip__in-cart';
                } else if (sessionAvailability.groupBookingCount !== 0) {
                    sessionClass += ' chip chip__group-booking';
                } else if (event.status === EntityStatus.INACTIVE) {
                    sessionClass += ' chip chip__inactive-event';
                }
            }

            return {
                ...session,
                startHour,
                startMinute,
                endTime,
                durationInMinutes,
                startTimeMinutes,
                endTimeMinutes,
                sessionClass,
            };
        });

        // Calculate tracks for all sessions in this event.
        const tracks = assignSessionsToTracks(preparedSessions);
        trackCountByEvent[eventID] = tracks.length;

        // Distribute tracks across cells - only add to starting cell.
        tracksByEventAndCell[eventID] = {};

        tracks.forEach((track, trackIndex) => {
            track.forEach((session) => {
                const startCell = getCellFromTime(session.startHour, session.startMinute);

                if (!tracksByEventAndCell[eventID][startCell]) {
                    tracksByEventAndCell[eventID][startCell] = [];
                }

                // Ensure the track array exists.
                while (tracksByEventAndCell[eventID][startCell].length <= trackIndex) {
                    tracksByEventAndCell[eventID][startCell].push([]);
                }

                // Calculate offset within the starting cell.
                const cellStartMinutes = (startCell - 1) * 30;
                const offsetInCell = session.startTimeMinutes - cellStartMinutes;

                const minutesRemainingInDayAtStartTime = 1440 - session.startTimeMinutes;
                const sessionMinutesInDay = Math.min(session.durationInMinutes, minutesRemainingInDayAtStartTime);

                tracksByEventAndCell[eventID][startCell][trackIndex].push({
                    ...session,
                    styleObject: {
                        width: (sessionMinutesInDay * 2) + 'px',
                        marginLeft: (offsetInCell * 2) + 'px',
                    },
                });
            });
        });
    });

    return {
        tracksByEventAndCell,
        trackCountByEvent,
    };
};

/**
 * @private
 *
 * Can we place the session into the provided track?
 * We can place it in the current track if it doesn't overlap with any sessions already in the track.
 *
 * @param {Session} session
 * @param {Session[]} track
 *
 * @return {boolean}
 */
const canPlaceInTrack = (session, track) => {
    return track.every((existingSession) => {
        return session.endTimeMinutes <= existingSession.startTimeMinutes
            || session.startTimeMinutes >= existingSession.endTimeMinutes;
    });
};

/**
 * @private
 *
 * Return a string that is used to construct the style for a given session block.
 *
 * @param {Session} session
 * @param {SessionAvailability} sessionAvailability
 * @param {Event} event
 *
 * @return {string}
 */
const getAvailabilityState = (session, sessionAvailability, event) => {
    let spacesAvailable = sessionAvailability.spacesAvailable;
    if (sessionAvailability.spacesBookable !== null) {
        spacesAvailable = sessionAvailability.spacesBookable;
    }
    if (spacesAvailable === 0) {
        return 'sold-out';
    }
    const threshold = Math.round((spacesAvailable / session.capacity) * 100);

    if (threshold > event.yellowThreshold) {
        return 'available';
    }
    if (threshold > event.amberThreshold) {
        return 'limited';
    }
    if (threshold > event.redThreshold) {
        return 'low';
    }
    return 'very-low';
};

/**
 * @private
 *
 * We split half hour slots of the day into 48 numbered slots or cells.
 *
 * @param {number} hour
 * @param {number} minute
 *
 * @return {number}
 */
const getCellFromTime = (hour, minute) => {
    return Math.floor((hour * 60 + minute) / 30) + 1;
};

/**
 * @private
 *
 * Utility function to get the duration of a session in minutes.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 *
 * @return {number}
 */
const getDurationInMinutes = (startDate, endDate) => {
    return Math.floor((endDate.getTime() - startDate.getTime()) / 60000);
};

/**
 * Sort sessions by filtered eventIDs and group them by eventID.
 *
 * @param {Session[]} sessions
 * @param {number[]} filteredEventIDs
 *
 * @return {Object.<number, Session[]>} - Object with eventIDs as keys and arrays of Session objects as values.
 */
const sortSessionsByFilteredEventIDs = (sessions, filteredEventIDs) => {
    // Note: using toSorted() rather than sort() so we don't mutate the original array which is sorted in the order
    // required for List View in TodayView.setSessions().
    const sortedSessions = sessions.toSorted((a, b) => {
        // Get the index of each eventID in the order array.
        const indexA = filteredEventIDs.indexOf(a.eventID);
        const indexB = filteredEventIDs.indexOf(b.eventID);

        // If eventIDs are different, sort by their order in the eventIDOrder array.
        if (indexA !== indexB) {
            // Handle case where eventID is not in the order array (put at end).
            if (indexA === -1) {
                return 1;
            }
            if (indexB === -1) {
                return -1;
            }
            return indexA - indexB;
        }

        // If eventIDs are the same, sort by startTime.
        return a.startTime - b.startTime;
    });

    const sessionsGroupedByEventID = sortedSessions.reduce((acc, session) => {
        if (!acc[session.eventID]) {
            acc[session.eventID] = [];
        }
        acc[session.eventID].push(session);
        return acc;
    }, {});

    return sessionsGroupedByEventID;
};

module.exports = {
    calculateSessionTracks,
    sortSessionsByFilteredEventIDs,
};
